/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { token } from '@atlaskit/tokens';
import { Flex, Box, xcss } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
	ReactionsContext,
	useCommentsContentState,
	useCommentsContentActions,
} from '@confluence/comment-context';
import { useSessionData } from '@confluence/session-data';
import { ProfileAvatar } from '@confluence/profile';
import { PageCommentEditor, type CommentSaveHandler } from '@confluence/comment-simple';
import { fg } from '@confluence/feature-gating';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	startEditorExperiences,
	ExperienceTrackerContext,
	ExperienceTimeout,
	startEditorSubExperience,
	VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE,
	ExperienceStart,
} from '@confluence/experience-tracker';
import { CommentsType, type GraphQLContentStatus } from '@confluence/comments-panel-queries';
import {
	Platform,
	ContentRepresentation,
} from '@confluence/page-comments-queries/entry-points/__types__/CreateGeneralCommentMutation';
import { updateApolloCacheParentCallback } from '@confluence/page-comments-queries/entry-points/pageCommentUtils';
import { usePageContentId, useContentType } from '@confluence/page-context';
import { useSearchSessionId } from '@confluence/search-session';
import { CommentType, useCommentsDataActions } from '@confluence/comments-data';
import type { CommentData } from '@confluence/comments-data';
import { useUnreadCommentsActions } from '@confluence/unread-comments';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode';
import { PageMode } from '@confluence/page-utils/entry-points/enums';
import { END } from '@confluence/navdex';
import { useCommentsPanelScroll, useCommentsPanelActions } from '@confluence/comments-panel-utils';
import { handlePanelSelectionAndScroll } from '@confluence/comments-panel';
import {
	AnalyticsSource,
	CommentType as AnalyticsCommentType,
} from '@confluence/comments-util/entry-points/analytics';
import { LoadablePaint } from '@confluence/loadable';

import type {
	ObjectAddCommentContainerUserQuery as ObjectAddCommentContainerUserQuery$data,
	ObjectAddCommentContainerUserQueryVariables as ObjectAddCommentContainerUserQuery$variables,
} from './__types__/ObjectAddCommentContainerUserQuery';
import type {
	ObjectAddCommentContainerCreateGeneralCommentMutation as ObjectAddCommentContainerCreateGeneralCommentMutation$data,
	ObjectAddCommentContainerCreateGeneralCommentMutationVariables as ObjectAddCommentContainerCreateGeneralCommentMutation$variables,
} from './__types__/ObjectAddCommentContainerCreateGeneralCommentMutation';
import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions';

const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const ObjectAddCommentContainerCreateGeneralCommentMutation = gql`
	mutation ObjectAddCommentContainerCreateGeneralCommentMutation(
		$pageId: ID!
		$cloudId: ID!
		$input: CreateCommentInput!
	) {
		createFooterComment(cloudId: $cloudId, input: $input) {
			id
			author {
				displayName
				profilePicture {
					path
				}
				permissionType
				... on KnownUser {
					accountId
				}
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				webui
				editui
			}
			location {
				type
				... on InlineComment {
					inlineMarkerRef
					inlineText
					inlineResolveProperties {
						isDangling
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
				... on FooterComment {
					commentResolveProperties {
						resolvedTime
						resolvedFriendlyDate
						resolvedUser
						resolved
					}
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment") {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					emojiId
					count
					reacted
				}
			}
			version {
				by {
					displayName
				}
				when
				number
			}
		}
	}
`;
const ObjectAddCommentContainerUserQuery = gql`
	query ObjectAddCommentContainerUserQuery($id: ID!) {
		user: confluence_atlassianUser(id: $id) {
			id
			displayName
			photos {
				value
			}
		}
	}
`;

// TODO: remove when cleaning up `confluence_frontend_comments_figma_style_alignment`
const oldAddCommentContainerStyles = xcss({
	position: 'sticky',
	bottom: 'space.0',
	display: 'flex',
	width: '100%',
	padding: 'space.200',
	borderTop: `1px solid ${token('color.border')}`,
	backgroundColor: 'color.background.input',
});

const addCommentContainerStyles = xcss({
	position: 'sticky',
	bottom: 'space.0',
	display: 'flex',
	width: '100%',
	padding: 'space.200',
	borderTop: `1px solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface',
});

const addCommentClosedContainerStyles = xcss({
	width: '100%',
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	flexShrink: 0,
});

const addCommentOpenContainerStyles = xcss({
	display: 'flex',
	flexDirection: 'row',
	width: '100%',
	gap: 'space.200',
	alignItems: 'flex-start',
});

const addCommentEditorStyles = xcss({
	width: '100%',
});

const avatarStyles = xcss({
	width: '32px',
	height: '32px',
	float: 'left',
});

const addCommentReactionsContainerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
});

export const ObjectAddCommentContainer = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}) => {
	const [isObjectAddCommentAddFocused, setIsObjectAddCommentAddFocused] = useState<boolean>(false);

	const { userId, edition } = useSessionData();
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer, closeCommentContainer } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();
	const { setCurrentlySelectedThreadKey } = useCommentsPanelActions();
	const [createGeneralCommentFn] = useMutation<
		ObjectAddCommentContainerCreateGeneralCommentMutation$data,
		ObjectAddCommentContainerCreateGeneralCommentMutation$variables
	>(ObjectAddCommentContainerCreateGeneralCommentMutation);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isReactionsEnabled } = useContext(ReactionsContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const pageMode = useGetPageMode();
	const [{ searchSessionId }] = useSearchSessionId();
	const { hasContentChanged } = useCommentsContentState();
	const { onChange, resetContentChanged } = useCommentsContentActions();
	const { addNewCommentThreads } = useCommentsDataActions();
	const { updateReadCommentsListState } = useUnreadCommentsActions();
	const { scrollCommentsPanelToThread } = useCommentsPanelScroll();

	const objectAddCommentContainerId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		if (fg('confluence_frontend_comments_a11y_kb')) {
			setIsObjectAddCommentAddFocused(true);
		}
		startEditorExperiences(
			{
				name: ADD_PAGE_COMMENT_EXPERIENCE,
				attributes: {
					editor: 'FABRIC',
					isSingleExperience: true,
					edition,
					source: AnalyticsSource.PAGE_FOOTER,
				},
			},
			{
				name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
			},
			{
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			},
			true,
		);

		expandCommentContainer();
	}, [edition, expandCommentContainer, setIsObjectAddCommentAddFocused]);

	const handleCommentsPanelSave: CommentSaveHandler = (adfObject, onSuccess) => {
		const pageId = contentId || '';
		const { adf } = adfObject as any;

		if (fg('confluence_frontend_experience_trace_id')) {
			startEditorSubExperience({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				timeout: ExperienceTimeout.EDITOR_PUBLISH,
			});
		} else {
			experienceTracker.start({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				timeout: ExperienceTimeout.EDITOR_PUBLISH,
			});
		}

		return createGeneralCommentFn({
			variables: {
				pageId,
				cloudId,
				input: {
					commentBody: {
						value: JSON.stringify(adf),
						representationFormat: ContentRepresentation.ATLAS_DOC_FORMAT,
					},
					commentSource: Platform.WEB,
					containerId: pageId,
				},
			},
			update: updateApolloCacheParentCallback(
				'create',
				{
					pageId,
					contentStatus: ['DRAFT', 'CURRENT'] as GraphQLContentStatus[],
					type: [CommentsType.FOOTER],
				},
				isReactionsEnabled,
			),
		}).then(({ data }: any) => {
			const commentResult = data.createFooterComment;
			const comment: CommentData = {
				...commentResult,
				replies: [], // not included in the payload
				isUnread: false,
				isOpen: true,
				type: CommentType.GENERAL,
			};

			updateReadCommentsListState((prev) => new Set([...prev, comment.id]));
			addNewCommentThreads({ inline: {}, general: { [comment.id]: comment } });

			onSuccess && onSuccess();
			resetContentChanged();
			closeCommentContainer();

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'created',
					actionSubject: 'comment',
					actionSubjectId: comment.id, // The newly created comment ID
					objectType: contentType,
					objectId: contentId,
					source: AnalyticsSource.PAGE_FOOTER,
					attributes: {
						commentType: AnalyticsCommentType.PAGE,
						source: AnalyticsSource.PAGE_FOOTER,
						pageMode,
						isReply: false,
						pageType: contentType,
						isLivePage: pageMode === PageMode.LIVE,
						editor: 'v2',
						searchSessionId,
						navdexPointType: END,
						parentCommentId: null, // analytics event schema type expects string or null
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
			});

			setTimeout(() => {
				handlePanelSelectionAndScroll({
					threadKey: comment.id,
					isResolved: false,
					pageMode,
					setCurrentlySelectedThreadKey,
					eventEmitter: undefined,
					commentType: CommentType.GENERAL,
					scrollCommentsPanelToThread,
					shouldScrollCommentsPanel: true,
				});
			}, 1000);
		});
	};

	const handleCancel = ({ isEditorAborted }: { isEditorAborted: boolean }) => {
		const doCancel = () => {
			if (isEditorAborted) {
				shouldAutofocusPromptRef.current = true;
				experienceTracker.abort({
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					reason: 'aborted because comment was cancelled',
				});
			}

			closeCommentContainer();
		};

		if (hasContentChanged) {
			resetContentChanged();
		}
		doCancel();
	};

	const handleOnEditorLoaded = () => {
		experienceTracker.succeed({
			name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
		});

		const footerCommentContainer = document.getElementById(objectAddCommentContainerId);

		if (!footerCommentContainer) {
			return;
		}

		footerCommentContainer.scrollIntoView({
			behavior: 'smooth',
			block: 'end',
			inline: 'nearest',
		});
	};
	const { data: userData } = useQuery<
		ObjectAddCommentContainerUserQuery$data,
		ObjectAddCommentContainerUserQuery$variables
	>(ObjectAddCommentContainerUserQuery, {
		variables: { id: userId || '' },
		fetchPolicy: 'cache-and-network',
		// Skip the query if data is provided or the user is anonymous
		skip: !userId,
	});
	const canViewProfile = true;

	const userInfo = {
		avatarUrl: userData?.user?.photos?.[0]?.value || '',
		accountId: userId,
		displayName: userData?.user?.displayName || '',
	};

	return (
		<Box
			id={objectAddCommentContainerId}
			xcss={
				fg('confluence_frontend_comments_figma_style_alignment')
					? addCommentContainerStyles
					: oldAddCommentContainerStyles
			}
			data-testId={objectAddCommentContainerId}
		>
			{!expanded ? (
				<Box xcss={addCommentClosedContainerStyles}>
					<ObjectAddCommentPrompt
						onClick={handleExpandCommentContainer}
						autofocusRef={shouldAutofocusPromptRef}
						// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
						{...(fg('confluence_frontend_comments_a11y_kb')
							? {
									onFocus: () => {
										// Only open the add comment editor if it is not already focused - this is to prevent
										// the prompt from opening immediately after the cancels the editor
										if (!isObjectAddCommentAddFocused) {
											handleExpandCommentContainer();
										}
									},
									onBlur: () => setIsObjectAddCommentAddFocused(false),
								}
							: {
									onFocus: () => {},
									onBlur: () => {},
								})}
					/>
					<Flex gap="space.050">
						{showLabels && fg('confluence_frontend_labels_in_footer') && (
							<>
								<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
								<FooterLabelsLoader />
							</>
						)}
						{showReactions && (
							<Box xcss={addCommentReactionsContainerStyles}>
								<ObjectCommentPageReactions
									isOffline={isOffline}
									optimisticReactions={optimisticReactions}
								/>
							</Box>
						)}
					</Flex>
				</Box>
			) : (
				<Box xcss={addCommentOpenContainerStyles}>
					<Box xcss={avatarStyles}>
						<ProfileAvatar
							canViewProfile={canViewProfile}
							src={userInfo.avatarUrl}
							userId={userInfo.accountId}
							isAnonymous={!userInfo.accountId}
							name={userInfo.displayName}
							testId="profile-avatar-comment"
						/>
					</Box>
					<Box xcss={addCommentEditorStyles}>
						<PageCommentEditor
							mode="create"
							saveHandler={handleCommentsPanelSave}
							cancelHandler={handleCancel}
							onEditorLoaded={handleOnEditorLoaded}
							onEditorContentChanged={onChange}
							useNewExperienceAbort
							expandEditor
							isFooterComment
							isOffline={isOffline}
						/>
					</Box>
				</Box>
			)}
		</Box>
	);
};
